// CO - Opções de kit — comportamento do bloco conso-kit-opcoes.liquid.
//
// Fork do conso-desconto-progressivo.js. Herda a mecânica de mover o painel
// (selects + botão) para o card marcado e de montar o carrinho numa chamada só.
//
// A DIFERENÇA É O CARD "1 UNIDADE".
// No bloco original todo card abre painel. Aqui comprar UMA peça é o fluxo nativo da
// PDP (<variant-picker> + botão do tema), então este JS faz o que o original não
// fazia: governa esses dois elementos. Como, depende do layout.
//
//   empilhado — os nativos ficam onde o tema os pôs, logo abaixo do bloco.
//     "1 unidade" -> painel na garagem (escondida), nativos VISÍVEIS.
//     "Kit N"     -> painel no card, nativos OCULTOS por classe no ancestral.
//
//     Classe num ancestral e não `style.display` nos elementos porque o Horizon
//     re-renderiza pedaços da PDP (morph do product-price, Section Rendering API na
//     troca de variante) e um estilo inline nosso seria varrido no caminho.
//     `.product-details` está fora desses alvos, então a classe sobrevive.
//
//   abas — os nativos são MOVIDOS pra dentro da aba de 1 unidade (#moverNativos) e
//     ficam lá. Quem os mostra e esconde é a aba abrindo e fechando, então a classe
//     no ancestral não entra em ação (ela apagaria a aba inteira). O mesmo re-render
//     que justifica a classe pode recriar o picker no lugar de origem — daí o
//     MutationObserver, que reacomoda.
//
// Nunca há duas UIs de variante na tela ao mesmo tempo.
//
// O painel é MOVIDO, nunca clonado nem recriado — é o que faz a escolha de cada peça
// sobreviver a ir pra unidade e voltar pro kit.

if (!window.__consoKitOpcoesInit) {
  window.__consoKitOpcoesInit = true;

  const SOURCE_ID = 'conso-kit-opcoes';

  // Escolha do cliente (1 unidade x kit) por bloco, chave = `name` dos rádios.
  // A troca de variante re-renderiza o bloco com o HTML do servidor, que marca o
  // degrau PADRÃO — com `degrau_padrao: kit`, trocar a cor no fluxo de 1 unidade
  // jogava o cliente de volta pro kit. Guardada aqui, a escolha é reaplicada.
  const ESCOLHA = new Map();
  const EVENTO_V4 = 'shopify:cart:lines-update';
  const ERRO_GENERICO = 'Não foi possível adicionar ao carrinho. Tente novamente.';

  // Classe de estado no ancestral, lida pelo conso-kit-opcoes.css.
  const CLASSE_KIT = 'co-ko-kit-ativo';
  // Classe aplicada aos seletores extras do setting.
  const CLASSE_OCULTO = 'co-ko-oculto';
  // Modo "preço acima das abas": com o kit marcado, esconde o <product-price> do tema.
  const CLASSE_PRECO_KIT = 'co-ko-preco-kit';

  const cartAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const cartUrl = () => `${window.Theme?.routes?.cart_url || '/cart'}.js`;

  const getCartSectionIds = () => {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.getAttribute('data-section-id'))
      .filter(Boolean)
      .filter((sectionId, index, all) => all.indexOf(sectionId) === index);
  };

  // Espelha o `animateAddToCart` do product-form.js: um teto fixo dá o mesmo
  // efeito sem depender do `animationend` — se a animação não rodar (reduced
  // motion), o botão volta ao normal do mesmo jeito.
  const ADDED_RESET_MS = 1000;

  class ConsoKitOpcoes extends HTMLElement {
    #inFlight = false;
    #resetTimer = null;

    connectedCallback() {
      if (this.__consoBound) return;
      this.__consoBound = true;

      this.addEventListener('change', this.#handleChange);
      this.addEventListener('click', this.#handleClick);

      // O navegador pode restaurar a escolha das peças (bfcache, autofill) sem
      // disparar change: sem isto a peça escolhida seguiria cinza de "vazia".
      this.#selects.forEach((select) => {
        if (select.value) select.classList.remove('co-ko__select--vazio');
      });

      // Sincroniza com o rádio marcado no HTML: o Liquid já imprime o painel no
      // card padrão, mas o navegador pode ter restaurado outro rádio (bfcache,
      // autofill) — aí o painel precisa mudar de lugar e o nativo, de estado.
      this.#moverNativos();
      this.#observarNativos();
      this.#plantarPrecoAcima();

      this.#restaurarEscolha();
      this.#abrir(this.#cardMarcado, false);
      this.#applyTier(this.#quantity);
      this.#vigiarEscolha();
    }

    get #chaveEscolha() {
      return this.querySelector('[data-co-ko-tier]')?.name || '';
    }

    /** Remarca o rádio que o cliente escolheu. true = mudou algo. */
    #restaurarEscolha() {
      const q = ESCOLHA.get(this.#chaveEscolha);
      if (q == null) return false;
      const radio = this.querySelector(`[data-co-ko-tier][value="${q}"]`);
      if (!radio || radio.checked) return false;
      radio.checked = true;
      return true;
    }

    // O morph do Horizon reescreve o atributo `checked` dos rádios sem recriar o
    // elemento (connectedCallback não roda de novo). Vigia só esse atributo.
    #vigiarEscolha() {
      this.__obsEscolha = new MutationObserver(() => {
        if (this.__restaurando) return;
        this.__restaurando = true;
        requestAnimationFrame(() => {
          if (this.#restaurarEscolha()) {
            this.#abrir(this.#cardMarcado, false);
            this.#applyTier(this.#quantity);
          }
          this.__restaurando = false;
        });
      });
      this.__obsEscolha.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked'] });
    }

    disconnectedCallback() {
      this.__obs?.disconnect();
      this.__obsEscolha?.disconnect();
      this.#ancestral?.classList.remove(CLASSE_PRECO_KIT);
      // A PDP pode trocar de produto sem recarregar (view transition). Deixar a
      // classe pendurada esconderia o picker do produto seguinte.
      this.#governarNativo(1);
      this.#trocarTitulo(1);
    }

    get #painel() {
      return this.querySelector('[data-co-ko-painel]');
    }

    get #garagem() {
      return this.querySelector('[data-co-ko-garagem]');
    }

    /** O card cujo rádio está marcado, ou null. */
    get #cardMarcado() {
      return this.querySelector('[data-co-ko-tier]:checked')?.closest('[data-co-ko-card]') ?? null;
    }

    /** O container onde a classe de estado é aplicada. */
    get #ancestral() {
      const seletor = this.dataset.seletorAncestral || '.product-details';
      return this.closest(seletor) || this.parentElement;
    }

    /** Ponto de ancoragem dentro da aba "1 unidade". Só existe no layout de abas. */
    get #ancoraNativa() {
      return this.querySelector('[data-co-ko-nativo]');
    }

    // Traz o preço, o <variant-picker> e o CTA do tema pra DENTRO da aba de 1 unidade.
    // A ordem da lista é a ordem em que aparecem lá dentro.
    //
    // Mover, e não clonar: são custom elements com estado (variante marcada,
    // listeners, form). Um clone nasceria morto e passaria a divergir do original.
    //
    // Idempotente de propósito — o observer chama de novo depois de cada
    // re-render do tema, e um elemento que já está no lugar é ignorado.
    #moverNativos() {
      const destino = this.#ancoraNativa;
      if (!destino) return;

      const escopo = this.#ancestral || document;
      const seletores = (this.dataset.seletoresNativo || 'product-price, variant-picker, .buy-buttons-block')
        .split(',').map((s) => s.trim()).filter(Boolean);

      for (const seletor of seletores) {
        let el;
        try { el = escopo.querySelector(seletor); } catch { continue; }
        // `destino.contains(el)` cobre o caso já-movido; sem isso o appendChild
        // reordenaria os elementos a cada chamada do observer.
        if (el && !destino.contains(el) && !el.contains(this)) destino.appendChild(el);
      }
    }

    // Modo "preço acima das abas": o preço do kit vive como irmão logo depois do
    // <product-price> do tema, pra herdar a mesma posição e tipografia. Irmão e não
    // filho: o morph do Horizon reescreve o conteúdo do <product-price> na troca de
    // variante e levaria o nosso junto.
    get #precoAcima() {
      return this.__precoAcima ?? this.querySelector('[data-co-ko-preco-acima]');
    }

    get #precoTema() {
      const escopo = this.#ancestral || document;
      return Array.from(escopo.querySelectorAll('product-price')).find((el) => !this.contains(el)) ?? null;
    }

    #plantarPrecoAcima() {
      const acima = this.querySelector('[data-co-ko-preco-acima]');
      if (!acima) return;
      this.__precoAcima = acima;

      const tema = this.#precoTema;
      if (!tema) return;
      // Mesmas classes de tipografia do bloco de preço (h3, paragraph...), menos o
      // espaçamento, que já vem do grupo onde ele mora.
      acima.className = `${tema.className} co-ko__preco-acima`;
      tema.after(acima);
    }

    #trocarPrecoAcima(quantity) {
      const acima = this.#precoAcima;
      if (!acima) return;

      const kit = quantity > 1;
      acima.hidden = !kit;
      for (const item of acima.querySelectorAll('[data-qtd]')) {
        item.hidden = Number(item.dataset.qtd) !== quantity;
      }
      this.#ancestral?.classList.toggle(CLASSE_PRECO_KIT, kit);
    }

    // Setting "Título da PDP com o kit": com o kit marcado o <h1> do produto vira
    // "Kit 3 Camisas ...". O texto já vem pronto do Liquid (plural resolvido), só
    // falta o {n}. Trocar o texto direto no <h1> é seguro porque a troca de variante
    // só faz morph do picker — o título não é re-renderizado.
    #trocarTitulo(quantity) {
      const modelo = this.dataset.tituloKit;
      if (!modelo) return;

      if (!this.__titulo) {
        const escopo = this.#ancestral || document;
        this.__titulo = Array.from(escopo.querySelectorAll('h1')).find((el) => !this.contains(el)) ?? null;
        if (!this.__titulo) return;
        this.__tituloOriginal = this.__titulo.textContent;
      }

      this.__titulo.textContent = quantity > 1
        ? modelo.replace('{n}', String(quantity))
        : this.__tituloOriginal;
    }

    // Setting "O que fica logo acima do botão": elementos da PDP (ex: provador e
    // medidas) que acompanham o CTA do estado marcado. Um elemento só, movido na
    // troca: não dá pra estar nos dois lugares, e clonar mataria listeners (o
    // provador abre modal).
    //
    //   abas      — acima do CTA nativo na aba de 1 unidade, do CTA do kit no kit.
    //   empilhado — acima do CTA do kit com o kit marcado; na unidade volta pro
    //               lugar de origem, marcado por um comentário na primeira mudança.
    #marcas = new Map();

    #posicionarAntesCta(quantity) {
      const seletores = (this.dataset.seletoresAntesCta || '')
        .split(',').map((s) => s.trim()).filter(Boolean);
      if (seletores.length === 0) return;

      let cta = null;
      if (quantity > 1) cta = this.querySelector('[data-co-ko-add]');
      else if (this.#ancoraNativa) cta = this.#ancoraNativa.querySelector('.buy-buttons-block');

      const escopo = this.#ancestral || document;
      for (const seletor of seletores) {
        let el;
        try { el = escopo.querySelector(seletor); } catch { continue; }
        if (!el || el.contains(this)) continue;

        if (!cta) {
          // Empilhado na unidade: devolve pra onde o tema pôs.
          const marca = this.#marcas.get(seletor);
          if (marca?.isConnected && marca.nextSibling !== el) marca.after(el);
          continue;
        }

        if (el.nextElementSibling === cta) continue;
        if (!this.#marcas.has(seletor) && !this.contains(el)) {
          const marca = document.createComment(`co-ko: ${seletor}`);
          el.before(marca);
          this.#marcas.set(seletor, marca);
        }
        cta.before(el);
      }
    }

    // O Horizon re-renderiza pedaços da PDP (Section Rendering API na troca de
    // variante). Se o picker for recriado no lugar de origem, ele volta pra fora
    // da aba — daí o observer, que só reage quando isso de fato acontece.
    // Mesmo padrão do conso-costume-color-gallery.
    #observarNativos() {
      const alvo = this.#ancestral;
      if (!alvo || !this.#ancoraNativa) return;

      this.__obs = new MutationObserver(() => {
        if (this.__reacomodando) return;
        this.__reacomodando = true;
        requestAnimationFrame(() => {
          this.#moverNativos();
          this.#posicionarAntesCta(this.#quantity);
          this.__reacomodando = false;
        });
      });
      this.__obs.observe(alvo, { childList: true, subtree: true });
    }

    // Liga/desliga o picker e o botão nativos. Quantidade 1 = fluxo nativo.
    #governarNativo(quantity) {
      if (this.dataset.ocultarNativo === 'false') return;

      const ativo = quantity > 1;
      const ancestral = this.#ancestral;

      // No layout de abas os nativos moram DENTRO da aba de 1 unidade: quem os
      // mostra e esconde é a própria aba abrindo e fechando. A classe no ancestral
      // apagaria a aba inteira quando o kit fosse marcado — só o empilhado a usa.
      if (ancestral && !this.#ancoraNativa) ancestral.classList.toggle(CLASSE_KIT, ativo);

      // Seletores extras do setting — elementos da PDP que só fazem sentido na
      // unidade (ex: contador de estoque). Valem nos dois layouts: ficam fora da
      // aba, então nada mais os esconderia.
      const extras = (this.dataset.seletoresOcultar || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (extras.length === 0) return;

      const escopo = ancestral || document;
      for (const seletor of extras) {
        let alvos;
        try {
          alvos = escopo.querySelectorAll(seletor);
        } catch {
          // Seletor inválido digitado no editor não pode derrubar o bloco.
          continue;
        }
        for (const alvo of alvos) {
          if (this.contains(alvo)) continue; // nunca esconder a si mesmo
          alvo.classList.toggle(CLASSE_OCULTO, ativo);
        }
      }
    }

    // Move o painel para o card escolhido e abre o card com animação. Mover o nó,
    // e não clonar, é o que faz a escolha de cada peça sobreviver à troca.
    //
    // O painel SÓ se move ao entrar num card de kit; ao voltar pra unidade ele fica
    // onde está, dentro do card que está recolhendo. Antes ele era mandado pra
    // garagem no mesmo instante em que o colapso começava, e o painel sumia de
    // supetão em vez de recolher junto. Ficar parado não custa nada: o card fechado
    // já está recortado e invisível, e os <select> seguem no DOM com a escolha.
    //
    // A garagem continua existindo pro estado inicial, quando o Liquid imprime o
    // painel fora dos cards porque a unidade nasce marcada.
    //
    // A classe entra num frame depois do appendChild: no mesmo frame o grid sairia
    // de 0fr para 1fr sem transição, porque o navegador ainda não pintou o estado
    // fechado com o conteúdo dentro.
    #abrir(card, animar = true) {
      const painel = this.#painel;

      const atual = this.querySelector('.co-ko__card--aberto');

      if (!card) {
        if (atual) atual.classList.remove('co-ko__card--aberto');
        return;
      }

      const quantity = Number(card.querySelector('[data-co-ko-tier]')?.value ?? 0);
      const alvo = quantity > 1 ? card.querySelector('[data-co-ko-corpo]') : null;

      if (painel && alvo && painel.parentElement !== alvo) alvo.appendChild(painel);

      const trocar = () => {
        // Fechar e abrir no MESMO frame. Quando o fechamento saía um frame na frente,
        // as duas alturas andavam fora de fase e o bloco mergulhava ~120px no meio da
        // volta pra unidade, empurrando a página. Como os dois painéis têm altura
        // parecida, em fase a altura total fica praticamente parada.
        if (atual && atual !== card) atual.classList.remove('co-ko__card--aberto');
        card.classList.add('co-ko__card--aberto');
      };

      if (!animar) {
        trocar();
        return;
      }

      requestAnimationFrame(trocar);
    }

    get #selects() {
      return Array.from(this.querySelectorAll('[data-co-ko-select]'));
    }

    get #slots() {
      return Array.from(this.querySelectorAll('[data-co-ko-slot]'));
    }

    /** Quantidade do card marcado. 1 = unidade (fluxo nativo). */
    get #quantity() {
      const checked = this.querySelector('[data-co-ko-tier]:checked');
      const value = Number(checked?.value ?? this.dataset.qtdPadrao ?? 1);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    #handleChange = (event) => {
      if (event.target.closest('[data-co-ko-tier]')) {
        ESCOLHA.set(this.#chaveEscolha, this.#quantity);
        this.#abrir(this.#cardMarcado);
        this.#applyTier(this.#quantity);
        return;
      }

      const select = event.target.closest('[data-co-ko-select]');
      if (!select) return;

      select.classList.remove('co-ko__select--vazio');
      if (select.getAttribute('aria-invalid') === 'true') {
        select.removeAttribute('aria-invalid');
        if (this.#pecasVazias(this.#quantity).length === 0) this.#setError('');
      }

      const thumb = select.closest('[data-co-ko-slot]')?.querySelector('[data-co-ko-thumb]');
      const src = select.selectedOptions[0]?.dataset.thumb;
      if (thumb && src) thumb.src = src;
    };

    #handleClick = (event) => {
      if (!event.target.closest('[data-co-ko-add]')) return;
      event.preventDefault();
      this.#add();
    };

    // Mostra os N primeiros slots, esconde o resto, escreve o N no botão e
    // sincroniza o picker nativo.
    #applyTier(quantity) {
      this.#slots.forEach((slot, index) => {
        slot.hidden = index >= quantity;
      });

      const texto = this.querySelector('[data-co-ko-add-texto]');
      const modelo = this.dataset.textoBotao || 'Adicionar {n} ao carrinho';
      if (texto) texto.textContent = modelo.replace('{n}', String(quantity));

      this.#governarNativo(quantity);
      this.#trocarPrecoAcima(quantity);
      this.#trocarTitulo(quantity);
      this.#posicionarAntesCta(quantity);
    }

    #setLoading(loading) {
      const button = this.querySelector('[data-co-ko-add]');
      if (!button) return;
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.classList.toggle('co-ko--loading', loading);
    }

    // As duas animações do CTA do tema: o burst de check no botão e a miniatura
    // voando até o ícone do carrinho. Moram no product-form.js, que este bloco
    // não usa, então são reproduzidas aqui.
    #animate() {
      if (this.dataset.animarAdd === 'false') return;

      const button = this.querySelector('[data-co-ko-add]');
      if (!button) return;

      button.dataset.added = 'true';
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => button.removeAttribute('data-added'), ADDED_RESET_MS);

      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const image = this.#selects[0]?.selectedOptions[0]?.dataset.thumb || this.dataset.thumbProduto;
      if (!cartIcon || !image || !customElements.get('fly-to-cart')) return;

      const flyToCart = document.createElement('fly-to-cart');
      flyToCart.classList.add('fly-to-cart--main');
      flyToCart.style.setProperty('background-image', `url(${image})`);
      flyToCart.style.setProperty('--start-opacity', '0');
      flyToCart.source = button;
      flyToCart.destination = cartIcon;
      document.body.appendChild(flyToCart);
    }

    #setError(message) {
      const box = this.querySelector('[data-co-ko-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    // Propriedade extra opcional por instância do bloco (setting
    // `propriedades_extra`, JSON de string->string). Usado pela Área de
    // Membros pra marcar a linha da promo "leve 3, pague 2" das camisetas —
    // o add nativo marca via form, mas este componente nunca submete form
    // nenhum, então sem isto a linha do kit chegava sem a property e a
    // function da promo não reconhecia a linha. Ausente = sem properties,
    // comportamento igual ao de antes em qualquer outro produto/página.
    #propriedadesExtra() {
      const raw = this.dataset.propriedadesExtra;
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }

    // Uma linha por variante distinta: a mesma variante em dois selects vira
    // quantity 2, e não duas linhas iguais na sacola. Produto sem variantes
    // (sem selects) manda N da única.
    #buildItems(quantity) {
      const properties = this.#propriedadesExtra();
      const single = Number(this.dataset.variantId);
      if (single && this.#selects.length === 0) {
        const item = { id: single, quantity };
        if (properties) item.properties = properties;
        return [item];
      }

      const items = [];

      for (const select of this.#selects.slice(0, quantity)) {
        const id = Number(select.value);
        if (!id) continue;

        const existing = items.find((item) => item.id === id);
        if (existing) existing.quantity += 1;
        else {
          const item = { id, quantity: 1 };
          if (properties) item.properties = properties;
          items.push(item);
        }
      }

      return items;
    }

    // Selects das N peças do kit ainda em "Selecione…" (setting comecar_vazio).
    #pecasVazias(quantity) {
      return this.#selects.slice(0, quantity).filter((select) => !select.value);
    }

    async #add() {
      if (this.#inFlight) return;

      const quantity = this.#quantity;

      // Peça sem escolha não pode ser pulada: o #buildItems ignoraria o select
      // vazio e o kit iria pra sacola com menos peças do que o botão anuncia.
      const vazias = quantity > 1 ? this.#pecasVazias(quantity) : [];
      if (vazias.length > 0) {
        vazias.forEach((select) => select.setAttribute('aria-invalid', 'true'));
        const opcoes = vazias[0].options[0]?.textContent.replace(/^Selecione\s+/i, '') || 'cor e tamanho';
        this.#setError(`Escolha ${opcoes} das ${quantity} peças.`);
        vazias[0].focus({ preventScroll: true });
        return;
      }

      const items = this.#buildItems(quantity);
      if (quantity < 2 || items.length === 0) return;

      this.#inFlight = true;
      this.#setLoading(true);
      this.#setError('');

      try {
        const body = { items };
        const sectionIds = getCartSectionIds();
        if (sectionIds.length > 0) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const payload = await response.json();

        // `/cart/add.js` devolve 422 com `description` quando uma variante ficou
        // sem estoque entre o render da página e o clique.
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || ERRO_GENERICO);
        }

        // Antes do GET /cart.js: a animação é feedback do clique e não deve
        // esperar um round-trip a mais para começar.
        this.#animate();

        const sections = payload.sections && typeof payload.sections === 'object' ? payload.sections : undefined;

        const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);

        const evento = new CustomEvent(EVENTO_V4, { bubbles: true });
        // Lidos direto do evento pelo cart-drawer.js e component-cart-items.js,
        // não do detail. `'add'` é o que abre a sacola.
        evento.action = 'add';
        evento.promise = Promise.resolve({
          cart,
          detail: {
            sections,
            itemCount: Number(cart?.item_count ?? 0),
            source: SOURCE_ID,
            didError: false,
          },
        });
        document.dispatchEvent(evento);
      } catch (error) {
        this.#setError(error?.message || ERRO_GENERICO);
      } finally {
        this.#inFlight = false;
        this.#setLoading(false);
        this.#applyTier(this.#quantity);
      }
    }
  }

  if (!customElements.get('conso-kit-opcoes')) {
    customElements.define('conso-kit-opcoes', ConsoKitOpcoes);
  }
}
